import { FC, useCallback } from 'react';
import { css } from '@emotion/react';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import { FieldsStateType } from '@/common/types/form';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import { getOptionValue } from 'views/companies/settings/helpers';
import config, {
  allow_invoice_linking_only_with_cases_of_same_or_connected_company,
  invoices_prefill_allocation_date_with,
  invoicesPreFilledAllocationDateOptions,
  InvoicesAllocationDateFillWith,
  invoices_ocr_engine,
  invoicesOCREngineOptions,
  InvoicesOCREngine,
  InvoicesType,
  reverse_accounting_journal_entries
} from './config';
import Checkbox from '@/library/components/form/inputs/Checkbox';
import Select from '@/library/components/form/inputs/select';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatBooleanDisplay, formatOptionDisplay } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type InvoicesProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.invoices;
const CATEGORY_TITLE = 'Invoices';

const LABELS = {
  [allow_invoice_linking_only_with_cases_of_same_or_connected_company]:
    'Allow Invoice Linking only with cases of the same or a connected company',
  [reverse_accounting_journal_entries]:
    'Allow reversing invoices that have been sent to accounting',
  [invoices_prefill_allocation_date_with]: 'Invoices Allocation Date',
  [invoices_ocr_engine]: 'OCR Engine'
};

const Invoices: FC<InvoicesProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { selectField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<InvoicesType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [allow_invoice_linking_only_with_cases_of_same_or_connected_company]:
        companySettings.allow_invoice_linking_only_with_cases_of_same_or_connected_company === 1,
      [reverse_accounting_journal_entries]:
        companySettings.reverse_accounting_journal_entries === 1,
      [invoices_prefill_allocation_date_with]:
        companySettings.invoices_prefill_allocation_date_with,
      [invoices_ocr_engine]: companySettings.invoices_ocr_engine
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as InvoicesType | null,
    toPayload: values => [
      {
        key: allow_invoice_linking_only_with_cases_of_same_or_connected_company,
        value: values[allow_invoice_linking_only_with_cases_of_same_or_connected_company] ? 1 : 0
      },
      {
        key: reverse_accounting_journal_entries,
        value: values[reverse_accounting_journal_entries] ? 1 : 0
      },
      {
        key: invoices_prefill_allocation_date_with,
        value: getOptionValue(values[invoices_prefill_allocation_date_with])
      },
      {
        key: invoices_ocr_engine,
        value: getOptionValue(values[invoices_ocr_engine])
      }
    ],
    labels: LABELS,
    formatValue: (key, value) => {
      if (
        key === allow_invoice_linking_only_with_cases_of_same_or_connected_company ||
        key === reverse_accounting_journal_entries
      ) {
        return formatBooleanDisplay(value);
      }
      if (key === invoices_prefill_allocation_date_with) {
        return formatOptionDisplay(value, invoicesPreFilledAllocationDateOptions);
      }
      if (key === invoices_ocr_engine) {
        return formatOptionDisplay(value, invoicesOCREngineOptions);
      }
      return formatOptionDisplay(value);
    },
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <div
        css={css`
          .form-field {
            width: 8.5rem;
          }
        `}
      >
        <SubCategory
          title="Linking"
          hidden={
            !isSettingVisible(
              CATEGORY_ID,
              allow_invoice_linking_only_with_cases_of_same_or_connected_company
            )
          }
          className="border-bottom border-gray-200"
        >
          <VisibleSetting
            categoryId={CATEGORY_ID}
            settingKey={allow_invoice_linking_only_with_cases_of_same_or_connected_company}
          >
            <div className="fs-12 mb-1 text-violet">Link Cases</div>
            <Checkbox
              className="mb-0"
              label={
                <InputLabel
                  title="Allow Invoice Linking only with cases of the same or a connected company"
                  apiKey={allow_invoice_linking_only_with_cases_of_same_or_connected_company}
                  isCheckbox
                />
              }
              labelClassName="fs-12 fw-medium text-primary"
              onChange={() =>
                selectField(allow_invoice_linking_only_with_cases_of_same_or_connected_company)(
                  !fields[allow_invoice_linking_only_with_cases_of_same_or_connected_company].value
                )
              }
              {...fields[allow_invoice_linking_only_with_cases_of_same_or_connected_company]}
            />
          </VisibleSetting>
        </SubCategory>

        <SubCategory
          title="Journals"
          hidden={!isSettingVisible(CATEGORY_ID, reverse_accounting_journal_entries)}
          className="border-bottom border-gray-200"
        >
          <VisibleSetting categoryId={CATEGORY_ID} settingKey={reverse_accounting_journal_entries}>
            <div className="fs-12 mb-1 mt-2 text-violet">Reverse Sent to Accounting</div>
            <Checkbox
              className="mb-0"
              label={
                <InputLabel
                  title="Allow reversing invoices that have been sent to accounting"
                  apiKey={reverse_accounting_journal_entries}
                  isCheckbox
                />
              }
              labelClassName="fs-12 fw-medium text-primary"
              onChange={() =>
                selectField(reverse_accounting_journal_entries)(
                  !fields[reverse_accounting_journal_entries].value
                )
              }
              {...fields[reverse_accounting_journal_entries]}
            />
          </VisibleSetting>
        </SubCategory>

        <SubCategory
          title="Allocation"
          hidden={!isSettingVisible(CATEGORY_ID, invoices_prefill_allocation_date_with)}
          className="border-bottom border-gray-200"
        >
          <VisibleSetting
            categoryId={CATEGORY_ID}
            settingKey={invoices_prefill_allocation_date_with}
          >
            <Select
              className="mt-2"
              label={
                <InputLabel
                  title="Invoices Allocation Date"
                  apiKey={invoices_prefill_allocation_date_with}
                  isCheckbox={false}
                />
              }
              options={invoicesPreFilledAllocationDateOptions}
              onChange={(option: InvoicesAllocationDateFillWith) =>
                selectField(invoices_prefill_allocation_date_with)(option)
              }
              {...fields[invoices_prefill_allocation_date_with]}
            />
          </VisibleSetting>
        </SubCategory>

        <SubCategory
          className="cpb-2 cmb-2"
          title="OCR"
          hidden={!isSettingVisible(CATEGORY_ID, invoices_ocr_engine)}
        >
          <VisibleSetting categoryId={CATEGORY_ID} settingKey={invoices_ocr_engine}>
            <Select
              className="mt-2"
              label={
                <InputLabel title="OCR Engine" apiKey={invoices_ocr_engine} isCheckbox={false} />
              }
              options={invoicesOCREngineOptions}
              onChange={(option: InvoicesOCREngine) => selectField(invoices_ocr_engine)(option)}
              {...fields[invoices_ocr_engine]}
            />
          </VisibleSetting>
        </SubCategory>
      </div>
    </Category>
  );
};

export default Invoices;
